// Registrar Devolução (ticket #27, ADR-0007; ADR-0019 para a baixa
// automática). É uma **ação sobre o Lote**, não uma entidade: grava a data, as
// quatro colunas por Tipo, cria o Sublote do que ficou retido e baixa o
// Backlog (Sublotes reais e/ou Backlog Inicial) do que voltou de carona —
// tudo numa operação só. Recusa se a carona passar do que existe no Backlog:
// o Backlog Inicial só é preenchido pela tela (ADR-0027).

import type { Id } from "./ids.ts";
import { depoisDeAmanha, formatData, horaDe, isDataISO, paraISO, type DataISO } from "./datas.ts";
import {
  alocarFifo,
  backlogNoMomento,
  chaveSaldo,
  devolvidaEntregue,
  saldoDisponivel,
  tiposBaixadosDoLote,
  type SaldoLinha,
} from "./backlog.ts";
import { contagensDoLote, definirRecebida, ErroLote, situacaoLote } from "./lotes.ts";
import { previstoDoLote } from "./paradas.ts";
import { situacaoRetencao, type SituacaoRetencao, type Trecho } from "./situacao-retencao.ts";
import { avisoCaronaEmTexto, avisoCaronaSemSaldo } from "./aviso-carona.ts";
import { emCorrecao, fechamentoDoLote, momentoDevolucao, REGISTRO_PENDENTE, type LoteFechamento } from "./fechamento-do-lote.ts";
import type {
  BaixaBacklog,
  BaixaBacklogInicial,
  ContagemLote,
  RascunhoFechamento,
  Retencao,
  Snapshot, DestinoBaixa } from "./snapshot.ts";

export class ErroDevolucao extends Error {}

/** A carona de um Tipo passou do que existia no Backlog até a devolução. */
export class ErroSemSaldoBacklog extends ErroDevolucao {
  readonly tipoNome: string;
  readonly falta: number;
  /** `aviso`: o texto de `avisoCaronaSemSaldo`, o mesmo que a Ficha mostra. */
  constructor(tipoNome: string, falta: number, aviso: string) {
    super(`${tipoNome}: ${aviso}`);
    this.tipoNome = tipoNome;
    this.falta = falta;
  }
}

export interface RetidaEntrada {
  motivo: string;
  quantidade: number;
}

export interface LinhaDevolucao {
  tipoId: Id;
  /**
   * O total físico entregue nesta visita — inclui tanto o que veio desta
   * Comanda quanto o que vier do Backlog de carona, somados. O que exceder o
   * que a Comanda ainda podia render (Recebida, ou a Conferida quando houver
   * uma — ver abaixo — menos o que ficou retido nesta mesma linha) só pode ter
   * vindo do Backlog: a diferença é calculada e alocada em FIFO sozinha
   * (ADR-0019). `ContagemLote.devolvida` grava só a parte própria — o que veio
   * de carona não conta na Divergência da Comanda (ADR-0007), só na Fatura,
   * separadamente.
   */
  devolvida: number;
  /** O que ficou, por motivo. Forma o Sublote. */
  retidas: RetidaEntrada[];
  /**
   * O que a lavanderia contou de fato (ADR-0021) — abre igual à Recebida, e o
   * operador só mexe quando a contagem real difere; a Recebida em si nunca
   * muda, é a prova do que o Cliente declarou. `undefined`/omitido ou igual à
   * Recebida = sem divergência, nada é gravado. Exige `conferidaMotivo`
   * quando difere da Recebida.
   */
  conferida?: number;
  /**
   * Motivo classificado da divergência entre a Conferida e a Recebida — um
   * valor de `listas.motivosDivergencia` (ADR-0021). Obrigatório só quando
   * difere.
   */
  conferidaMotivo?: string;
}

export interface DadosDevolucao {
  data: DataISO;
  /** "HH:MM" da devolução; opcional. */
  hora?: string;
  linhas: LinhaDevolucao[];
}

export interface OpcoesFechamento {
  /**
   * Confere a retenção de cada peça (ADR-0029, `situacaoRetencao`): a Retida
   * digitada na Coleta (a foto do Backlog do hotel, ADR-0023) contra Backlog
   * antes + (Conferida − Devolvida), e que toda peça que não voltou tem
   * motivo. Só no "Fechar OS" do operador e no Fechar em Massa; "Salvar" e as
   * recontas automáticas não conferem.
   */
  conferirRetencao?: boolean;
  /**
   * Refaz, em ordem de devolução, a carona das OS do mesmo Cliente devolvidas
   * depois desta e já gravadas (ADR-0028): o fechamento entra no sistema fora
   * de ordem, e a FIFO tem de seguir a data de devolução. Padrão: sim.
   */
  reordenar?: boolean;
  /**
   * Carimbo do registro no sistema ("AAAA-MM-DDTHH:MM:SS", relógio do
   * servidor) para uma OS que ainda não tem um — o primeiro "Fechar OS".
   * Desempata OS devolvidas na mesma data e hora (ADR-0028).
   */
  registradoEm?: string;
  /** Recusa sem a hora da devolução — "Fechar OS" e Fechar em Massa (ADR-0028). */
  exigirHora?: boolean;
  /**
   * "Hoje" de quem fecha: recusa devolução depois de amanhã (só a data,
   * `depoisDeAmanha`) — "Fechar OS" e Fechar em Massa. Uma data à frente tira as retidas desta OS do Backlog até
   * lá (ADR-0028), e as OS devolvidas antes ficam sem elas (OS 436).
   */
  hoje?: DataISO;
}

/** Soma das retenções de um Lote para um Tipo (inclui as retidas de novo). */
function retidoDoLote(snapshot: Snapshot, loteId: Id, tipoId: Id): number {
  return snapshot.retencoes
    .filter((r) => r.loteId === loteId && r.tipoId === tipoId)
    .reduce((a, r) => a + r.quantidade, 0);
}

/**
 * O Backlog do Cliente num Tipo logo antes da devolução de um Lote, na ordem
 * das devoluções (ADR-0028) — o ponto de partida de `situacaoRetencao`.
 * `base` sem a devolução deste Lote (`baseDoFechamento`). A Ficha e o "Fechar
 * OS" usam esta mesma função.
 */
export function backlogAntesDaDevolucao(
  base: Snapshot,
  loteId: Id,
  tipoId: Id,
  devolucao: { data: string; hora?: string | null; registradoEm?: string | null },
): number {
  const lote = base.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroDevolucao("Lote não encontrado.");
  return backlogNoMomento(base, lote.clienteId, tipoId, momentoDevolucao(lote, devolucao)).noMomento;
}

/**
 * OS do mesmo Cliente devolvidas depois de `momento` que levaram peças do
 * Backlog e ainda podem mudar (não faturadas nem canceladas), em ordem de
 * devolução — as que `registrarDevolucao` refaz (ADR-0028).
 */
function caronasPosteriores(snapshot: Snapshot, loteId: Id, clienteId: Id, momento: string): Id[] {
  const comCarona = new Set([
    ...snapshot.baixasBacklog.map((b) => b.loteId),
    ...snapshot.baixasBacklogInicial.map((b) => b.loteId),
  ]);
  return snapshot.lotes
    .filter(
      (l) =>
        l.id !== loteId &&
        l.clienteId === clienteId &&
        l.canceladoEm == null &&
        comCarona.has(l.id) &&
        momentoDevolucao(l) > momento &&
        situacaoLote(snapshot, l.id) !== "faturado",
    )
    .sort((a, b) => (momentoDevolucao(a) < momentoDevolucao(b) ? -1 : 1))
    .map((l) => l.id);
}

/**
 * O estado de onde a FIFO de um Fechamento parte: sem a devolução anterior
 * deste Lote e sem a carona das OS devolvidas depois dele (que serão refeitas
 * em seguida — ADR-0028). A Ficha usa o mesmo para o preview.
 */
export function baseDoFechamento(
  snapshot: Snapshot,
  loteId: Id,
  devolucao: { data: string; hora?: string | null; registradoEm?: string | null },
): Snapshot {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroDevolucao("Lote não encontrado.");
  const partida = limpar(snapshot, loteId);
  const posteriores = new Set(
    caronasPosteriores(partida, loteId, lote.clienteId, momentoDevolucao(lote, devolucao)),
  );
  if (posteriores.size === 0) return partida;
  return {
    ...partida,
    baixasBacklog: partida.baixasBacklog.filter((b) => !posteriores.has(b.loteId)),
    baixasBacklogInicial: partida.baixasBacklogInicial.filter((b) => !posteriores.has(b.loteId)),
  };
}

/** "dd/mm/aaaa hh:mm" da devolução de uma OS, para as mensagens. */
function formatarDevolucao(l: LoteFechamento): string {
  const f = fechamentoDoLote(l);
  const data = f?.data ?? "";
  const hora = f?.hora;
  const [a, m, d] = data.split("-");
  return `${d}/${m}/${a}${hora ? ` ${hora}` : ""}`;
}

/**
 * Tira do snapshot tudo o que uma Devolução anterior deste Lote tinha
 * gravado — o estado de onde `registrarDevolucao` recalcula do zero.
 * Exportada para a tela de Devolução montar o mesmo preview (saldo
 * disponível, de onde sai a baixa) que será realmente salvo ao reabrir uma
 * Devolução já registrada; sem isso o preview ficaria olhando pra um saldo
 * que já inclui as próprias baixas desta Devolução, subestimando o que vai
 * sobrar depois de `limpar` as desfazer.
 */
export function limpar(snapshot: Snapshot, loteId: Id): Snapshot {
  return {
    ...snapshot,
    retencoes: snapshot.retencoes.filter((r) => r.loteId !== loteId),
    baixasBacklog: snapshot.baixasBacklog.filter((b) => b.loteId !== loteId),
    baixasBacklogInicial: snapshot.baixasBacklogInicial.filter((b) => b.loteId !== loteId),
    contagens: snapshot.contagens.map((c) =>
      c.loteId === loteId
        ? { ...c, devolvida: 0, conferida: null, conferidaMotivo: null }
        : c,
    ),
  };
}

/**
 * "Reabrir Fechamento" + Salvar: a OS volta a Lançada até o próximo "Fechar
 * OS" — fora de Faturamento e Dashboard enquanto isso, como qualquer OS
 * Lançada. As contagens, retenções e baixas do fechamento continuam gravadas
 * (a Ficha reabre com elas para corrigir) e a data/hora dele vão para
 * `fechamentoAnteriorEm`/`Hora`. Vale para OS fechada ou de Fatura reaberta
 * (ADR-0024); faturada, só reabrindo a Fatura antes.
 */
export function reabrirFechamento(snapshot: Snapshot, loteId: Id): Snapshot {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroDevolucao("Lote não encontrado.");
  const situacao = situacaoLote(snapshot, loteId);
  if (situacao === "faturado") {
    throw new ErroDevolucao("Esta OS já foi faturada. Reabra a fatura para editar.");
  }
  if (situacao !== "fechado" && situacao !== "reaberta") {
    throw new ErroDevolucao("Só dá para reabrir o fechamento de uma OS fechada.");
  }
  return {
    ...snapshot,
    lotes: snapshot.lotes.map((l) =>
      l.id === loteId
        ? {
            ...l,
            fechamentoAnteriorEm: l.devolvidoEm,
            fechamentoAnteriorHora: l.devolvidoHora,
            devolvidoEm: null,
            devolvidoHora: null,
          }
        : l,
    ),
  };
}

/**
 * "Salvar" numa OS reaberta para correção: grava as correções do Fechamento
 * (contagens, retenções, carona do Backlog, data/hora) e mantém a OS
 * Lançada — só "Fechar OS" volta a Fechada. Sem isso as correções digitadas
 * eram descartadas e o Backlog seguia com o fechamento antigo (OS 767).
 */
export function salvarFechamentoEmCorrecao(
  snapshot: Snapshot,
  loteId: Id,
  dados: DadosDevolucao,
  opcoes: OpcoesFechamento = {},
): Snapshot {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroDevolucao("Lote não encontrado.");
  if (!emCorrecao(lote) || situacaoLote(snapshot, loteId) !== "lancado") {
    throw new ErroDevolucao("Só uma OS reaberta para correção grava o fechamento sem fechar.");
  }
  return reabrirFechamento(
    registrarDevolucao(snapshot, loteId, dados, { ...opcoes, conferirRetencao: false }),
    loteId,
  );
}

/**
 * Guarda o que foi lançado no Fechamento sem fechar a OS ("Salvar" numa OS
 * lançada). Não é Fechamento: nada entra no Backlog nem na Fatura — o
 * rascunho só volta para a Ficha, e o "Fechar OS" o substitui. Sem nenhuma
 * linha, apaga o rascunho. Valida na escrita o que pode ser validado de um
 * rascunho: Tipo existe e quantidades são inteiras e não negativas. Motivo
 * vazio é normal (ainda não escolhido); as regras do Fechamento (saldo do
 * Backlog, Retida digitada) só valem no "Fechar OS".
 */
export function guardarRascunhoFechamento(
  snapshot: Snapshot,
  loteId: Id,
  rascunho: RascunhoFechamento,
): Snapshot {
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) throw new ErroDevolucao("Lote não encontrado.");
  const situacao = situacaoLote(snapshot, loteId);
  if (situacao !== "lancado" || emCorrecao(lote)) {
    throw new ErroDevolucao("Só uma OS lançada guarda rascunho do Fechamento.");
  }
  const inteiro = (n: unknown): boolean => typeof n === "number" && Number.isInteger(n) && n >= 0;
  for (const l of rascunho.linhas) {
    if (!snapshot.tiposPeca.some((t) => t.id === l.tipoId)) {
      throw new ErroDevolucao("Tipo de peça não encontrado no rascunho do Fechamento.");
    }
    if (!inteiro(l.devolvida) || (l.conferida != null && !inteiro(l.conferida))) {
      throw new ErroDevolucao("Quantidade inválida no rascunho do Fechamento.");
    }
    if (l.retidas.some((r) => !inteiro(r.quantidade) || typeof r.motivo !== "string")) {
      throw new ErroDevolucao("Retida inválida no rascunho do Fechamento.");
    }
  }
  const guardado: RascunhoFechamento | null =
    rascunho.linhas.length === 0
      ? null
      : {
          data: rascunho.data,
          hora: rascunho.hora || null,
          linhas: rascunho.linhas.map((l) => ({
            tipoId: l.tipoId,
            devolvida: l.devolvida,
            retidas: l.retidas.map((r) => ({ motivo: r.motivo.trim(), quantidade: r.quantidade })),
            conferida: l.conferida,
            conferidaMotivo: l.conferidaMotivo?.trim() || null,
          })),
        };
  return {
    ...snapshot,
    lotes: snapshot.lotes.map((l) => (l.id === loteId ? { ...l, rascunhoFechamento: guardado } : l)),
  };
}

/**
 * Refaz o Fechamento de OS de um Lote já fechado depois que a Coleta dele
 * mudou (`editarLote`). Mantém o que foi entregue — o total digitado em
 * Devolvida (`devolvidaEntregue`), as retenções e as Conferidas que
 * sobreviveram — e deixa `registrarDevolucao` redividir entre a parte
 * própria e a carona do Backlog contra a Recebida nova. Sem isso, mudar a
 * Recebida de uma OS fechada deixava a divisão velha: parte própria acima da
 * Recebida, ou peças baixadas do Backlog sem necessidade. Também revalida a
 * data (a Coleta não pode passar da Devolução). Lote não fechado: nada muda.
 */
export function refazerFechamento(
  antes: Snapshot,
  depois: Snapshot,
  loteId: Id,
  opcoes: OpcoesFechamento = {},
): Snapshot {
  const lote = depois.lotes.find((l) => l.id === loteId);
  if (!lote || lote.canceladoEm != null) return depois;
  // fechada: a data do fechamento; reaberta para correção: a data guardada
  const fechamento = fechamentoDoLote(lote);
  if (fechamento == null) return depois;
  const { data, hora } = fechamento;
  const tipos = new Set<Id>([
    ...contagensDoLote(antes, loteId).map((c) => c.tipoId),
    ...antes.retencoes.filter((r) => r.loteId === loteId).map((r) => r.tipoId),
    ...tiposBaixadosDoLote(antes, loteId),
  ]);
  const linhas: LinhaDevolucao[] = [];
  for (const tipoId of tipos) {
    const devolvida = devolvidaEntregue(antes, loteId, tipoId);
    const retidas = antes.retencoes
      .filter((r) => r.loteId === loteId && r.tipoId === tipoId)
      .map((r) => ({ motivo: r.motivo, quantidade: r.quantidade }));
    const c = depois.contagens.find((x) => x.loteId === loteId && x.tipoId === tipoId);
    const conferida = c?.conferida ?? null;
    if (devolvida === 0 && retidas.length === 0 && conferida == null) continue;
    linhas.push({
      tipoId,
      devolvida,
      retidas,
      ...(conferida != null ? { conferida, conferidaMotivo: c?.conferidaMotivo ?? "" } : {}),
    });
  }
  const dados: DadosDevolucao = { data, ...(hora ? { hora } : {}), linhas };
  const semConferir = { ...opcoes, conferirRetencao: false };
  return lote.devolvidoEm != null
    ? registrarDevolucao(depois, loteId, dados, semConferir)
    : salvarFechamentoEmCorrecao(depois, loteId, dados, semConferir);
}

/**
 * O que já saiu do Sublote de uma OS: as OS que levaram peças dele (de
 * carona ou retidas de novo) e as Perdas registradas contra ele. É o que
 * excluir ou cancelar essa OS vai refazer (ADR-0026) — a tela mostra isso
 * na confirmação.
 */
export function saidasDoSublote(snapshot: Snapshot, loteId: Id): { lotes: Id[]; perdas: number } {
  const lotes = [
    ...new Set(
      snapshot.baixasBacklog.filter((b) => b.origemLoteId === loteId && b.loteId !== loteId).map((b) => b.loteId),
    ),
  ];
  const perdas = snapshot.perdas.filter((p) => p.origemLoteId === loteId).length;
  return { lotes, perdas };
}

/**
 * Excluir ou cancelar uma OS apaga o Sublote dela; o que já tinha saído
 * dele é refeito (ADR-0026):
 * - as OS que levaram peças dele são recalculadas com o mesmo total
 *   entregue, na ordem em que fecharam — a carona volta a sair do Backlog
 *   (FIFO); sem saldo que cubra, recusa (o Backlog Inicial não fica negativo
 *   sozinho — ADR-0027);
 * - as Perdas contra ele são apagadas (a linha de saldo deixou de existir).
 * Se alguma OS que levou peças já foi faturada, recusa: o faturamento dela
 * não pode mudar sem reabrir a Fatura.
 * `antes` é o Snapshot original; `depois`, o que a exclusão/cancelamento
 * já montou sem a OS (ou com ela cancelada).
 */
export function desfazerSaidasDoSublote(antes: Snapshot, depois: Snapshot, loteId: Id): Snapshot {
  const { lotes } = saidasDoSublote(antes, loteId);
  const faturadas = lotes
    .filter((id) => situacaoLote(antes, id) === "faturado")
    .map((id) => antes.lotes.find((l) => l.id === id)?.os);
  if (faturadas.length > 0) {
    throw new ErroDevolucao(
      `Peças retidas nesta OS saíram na OS ${faturadas.join(", ")}, que já foi faturada — reabra a fatura dela antes.`,
    );
  }
  let s: Snapshot = {
    ...depois,
    baixasBacklog: depois.baixasBacklog.filter((b) => b.origemLoteId !== loteId),
    perdas: depois.perdas.filter((p) => p.origemLoteId !== loteId),
  };
  // a mesma ordem do "Fechar OS" (ADR-0028), com o empate pelo registro
  const ordem = new Map(lotes.map((id) => [id, momentoDevolucao(antes.lotes.find((x) => x.id === id)!)]));
  for (const id of [...lotes].sort((a, b) => (ordem.get(a)! < ordem.get(b)! ? -1 : ordem.get(a)! > ordem.get(b)! ? 1 : 0))) {
    s = refazerFechamento(antes, s, id, { reordenar: false });
  }
  return s;
}

/**
 * O que a Comanda deste Lote ainda podia render depois de reservar o que
 * ficou retido — qualquer coisa de Devolvida acima disso só pode ter vindo do
 * Backlog de carona (ADR-0019). Exportada para a tela de Devolução mostrar o
 * mesmo cálculo ao vivo que `registrarDevolucao` vai realmente salvar.
 */
export function calcularExcedente(recebida: number, devolvida: number, retido: number): number {
  const d = dividirFechamento(recebida, devolvida, retido);
  return d.caronaDevolvida + d.caronaRetida;
}

/**
 * Como as Peças de um Tipo se dividem num Fechamento de OS (ADR-0025).
 * `disponivel` é o que a Comanda desta OS rende (Conferida, senão Recebida).
 * As retidas reservam primeiro as Peças da própria OS; o que passa do
 * disponível só pode ter vindo do Backlog — tanto nas devolvidas (carona
 * devolvida, faturada) quanto nas retidas (carona retida de novo: sai da
 * origem e fica no Sublote desta OS, sem faturar).
 */
export function dividirFechamento(
  disponivel: number,
  devolvida: number,
  retido: number,
): { devolvidaPropria: number; caronaDevolvida: number; caronaRetida: number } {
  const devolvidaPropria = Math.min(devolvida, Math.max(0, disponivel - retido));
  return {
    devolvidaPropria,
    caronaDevolvida: devolvida - devolvidaPropria,
    caronaRetida: Math.max(0, retido - disponivel),
  };
}

/**
 * Uma recusa da Prévia: a peça a que se refere (`null` = a OS inteira), o
 * erro e de que conta ela vem — "carona" (acima do Backlog, o texto está em
 * `PreviaTipo.carona.aviso`), "retencao" (`PreviaTipo.retencao.situacao`) ou
 * "geral" — para a tela desenhar com destaque sem reinterpretar o texto.
 */
export interface RecusaFechamento {
  tipoId: Id | null;
  erro: ErroDevolucao;
  natureza: "carona" | "retencao" | "geral";
}

/** O que a Prévia diz de um Tipo de Peça. */
export interface PreviaTipo {
  tipoId: Id;
  /** Parte própria × carona (ADR-0025); zeros quando o Tipo não tem linha. */
  divisao: { devolvidaPropria: number; caronaDevolvida: number; caronaRetida: number };
  /** De onde sai a carona (FIFO), quanto falta no Backlog e o aviso; `null` sem carona. */
  carona: { origem: SaldoLinha[]; falta: number; aviso: Trecho[][] | null } | null;
  /** Conta do Backlog do hotel e situação da retenção (ADR-0029); `null` sem contagem. */
  retencao: SituacaoRetencao | null;
  /**
   * A conta da peça que a Ficha mostra (#91): o que a Comanda rende (Conferida,
   * senão Recebida), o total devolvido, as retidas digitadas (com ou sem
   * motivo), o que veio do Backlog e o que ficou sem destino. Fecha sempre:
   * retida = doBacklog + rende − devolvida − semDestino (ADR-0023).
   */
  conta: ContaDaPeca;
}

export interface ContaDaPeca {
  rende: number;
  devolvida: number;
  retida: number;
  doBacklog: number;
  semDestino: number;
}

const contaDaPeca = (rende: number, devolvida: number, retida: number, doBacklog: number): ContaDaPeca => ({
  rende,
  devolvida,
  retida,
  doBacklog,
  // carona do Backlog (Devolvida acima do que a Comanda rende) não é sem destino
  semDestino: Math.max(0, rende - devolvida - retida),
});

/**
 * Prévia do Fechamento de OS (#83): a mesma conta que o "Fechar OS" faz —
 * divisão, carona do Backlog (FIFO), situação da retenção e as recusas —,
 * sem gravar e sem parar na primeira recusa. `recusas` vem na ordem em que o
 * "Fechar OS" recusaria (a primeira é a que ele dá); `resultado` é o que ele
 * grava, e só existe sem recusa. A Ficha e o `registrarDevolucao` usam esta
 * mesma função: a tela e a recusa nunca divergem.
 */
export function preverFechamento(
  snapshot: Snapshot,
  loteId: Id,
  dados: DadosDevolucao,
  opcoes: OpcoesFechamento = {},
): { tipos: Map<Id, PreviaTipo>; recusas: RecusaFechamento[]; resultado: Snapshot | null } {
  const tipos = new Map<Id, PreviaTipo>();
  const recusas: RecusaFechamento[] = [];
  const recusar = (tipoId: Id | null, erro: ErroDevolucao, natureza: RecusaFechamento["natureza"] = "geral"): void => {
    recusas.push({ tipoId, erro, natureza });
  };
  // recusa que impede qualquer conta: só ela, sem prévia por Tipo
  const somente = (erro: ErroDevolucao) => ({ tipos, recusas: [{ tipoId: null, erro, natureza: "geral" as const }], resultado: null });

  const reordenar = opcoes.reordenar ?? true;
  const lote = snapshot.lotes.find((l) => l.id === loteId);
  if (!lote) return somente(new ErroDevolucao("Lote não encontrado."));
  const situacao = situacaoLote(snapshot, loteId);
  if (situacao === "cancelado") return somente(new ErroDevolucao("Esta OS está cancelada."));
  if (situacao === "faturado") {
    return somente(new ErroDevolucao("Esta OS já foi faturada. Reabra a fatura para editar."));
  }
  // sem data não há conta: o Backlog e a FIFO dependem do momento (ADR-0028)
  if (!isDataISO(dados.data)) return somente(new ErroDevolucao("Informe a data da devolução."));
  if (dados.data < lote.coletaData) {
    recusar(null, new ErroDevolucao("A devolução não pode ser anterior à coleta."));
  } else if (opcoes.hoje != null && depoisDeAmanha(dados.data, opcoes.hoje)) {
    recusar(null, new ErroDevolucao(`A devolução não pode ser no futuro (${formatData(dados.data)}).`));
  }
  const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (dados.hora != null && dados.hora !== "" && !HORA.test(dados.hora)) {
    recusar(null, new ErroDevolucao("Hora da devolução inválida (use HH:MM)."));
  }
  if (opcoes.exigirHora && !dados.hora) {
    recusar(null, new ErroDevolucao("Informe a hora da devolução."));
  }

  // o momento desta devolução: a régua de tudo o que vem abaixo (ADR-0028)
  const devolucao = { data: dados.data, hora: dados.hora || null, registradoEm: opcoes.registradoEm ?? null };
  const momento = momentoDevolucao(lote, devolucao);

  // Peça retida aqui que já voltou em outra OS não pode ter sido retida
  // depois dessa volta: a devolução desta OS não passa da daquela.
  for (const b of snapshot.baixasBacklog) {
    if (b.origemLoteId !== loteId || b.loteId === loteId) continue;
    const outra = snapshot.lotes.find((l) => l.id === b.loteId);
    if (outra && momentoDevolucao(outra) <= momento) {
      recusar(
        null,
        new ErroDevolucao(
          `Peças retidas nesta OS saíram na OS ${outra.os}, devolvida em ${formatarDevolucao(outra)} — ` +
            "a devolução desta OS não pode ser depois disso.",
        ),
      );
      break;
    }
  }

  // parte-se do estado sem a Devolução anterior deste Lote — assim regravar não
  // conta duas vezes, e o Sublote do próprio Lote não vira fonte de baixa. Sem
  // também a carona das OS devolvidas depois desta (refeitas no fim): a FIFO
  // segue a data de devolução, não a ordem em que os fechamentos foram gravados.
  const posteriores = reordenar ? caronasPosteriores(snapshot, loteId, lote.clienteId, momento) : [];
  const partida = reordenar ? baseDoFechamento(snapshot, loteId, devolucao) : limpar(snapshot, loteId);
  const nomeDe = (tipoId: Id): string => partida.tiposPeca.find((t) => t.id === tipoId)?.nome ?? "peça";

  const vistos = new Set<Id>();
  const retencoes: Retencao[] = [];
  const baixas: BaixaBacklog[] = [];
  const baixasInicial: BaixaBacklogInicial[] = [];
  const devolvidaPorTipo = new Map<Id, number>();
  const conferidaPorTipo = new Map<Id, { conferida: number | null; motivo: string | null }>();
  // retidas digitadas com quantidade mas ainda sem motivo (só na Ficha):
  // não viram Sublote, mas contam para a sugestão do "+ Motivo"
  const pendentesPorTipo = new Map<Id, number>();
  // o que a Comanda rende por Tipo, como a divisão usou (Conferida digitada
  // mesmo sem motivo) — a situação da retenção usa a mesma base
  const disponivelPorTipo = new Map<Id, number>();

  for (const linha of dados.linhas) {
    if (!partida.tiposPeca.some((t) => t.id === linha.tipoId)) {
      recusar(linha.tipoId, new ErroDevolucao("Tipo de peça não encontrado."));
      continue;
    }
    if (vistos.has(linha.tipoId)) {
      recusar(linha.tipoId, new ErroDevolucao("O mesmo tipo de peça apareceu duas vezes."));
      continue;
    }
    vistos.add(linha.tipoId);

    if (!Number.isInteger(linha.devolvida) || linha.devolvida < 0) {
      recusar(linha.tipoId, new ErroDevolucao("Quantidade devolvida inválida."));
      continue;
    }

    // --- retidas: viram o Sublote deste Lote ---
    const porMotivo = new Map<string, number>();
    for (const r of linha.retidas) {
      const motivo = r.motivo.trim();
      if (!Number.isInteger(r.quantidade) || r.quantidade <= 0) {
        recusar(linha.tipoId, new ErroDevolucao(motivo === "" ? "Escolha o motivo da retenção." : "Quantidade retida inválida."));
        continue;
      }
      if (motivo === "") {
        recusar(linha.tipoId, new ErroDevolucao("Escolha o motivo da retenção."), "retencao");
        pendentesPorTipo.set(linha.tipoId, (pendentesPorTipo.get(linha.tipoId) ?? 0) + r.quantidade);
        continue;
      }
      porMotivo.set(motivo, (porMotivo.get(motivo) ?? 0) + r.quantidade);
    }
    for (const [motivo, quantidade] of porMotivo) {
      retencoes.push({ loteId, tipoId: linha.tipoId, motivo, quantidade });
    }
    const retidoLinha = [...porMotivo.values()].reduce((a, q) => a + q, 0);

    // --- conferida: o que a lavanderia contou de fato (ADR-0021) — a
    // Recebida em si nunca muda, é a prova do que o Cliente declarou. A conta
    // usa a Conferida digitada mesmo sem o motivo (a recusa pede o motivo).
    const recebida =
      partida.contagens.find((c) => c.loteId === loteId && c.tipoId === linha.tipoId)
        ?.recebida ?? 0;
    let conferida: number | null = null;
    let conferidaMotivo: string | null = null;
    let disponivel = recebida;
    if (linha.conferida != null && linha.conferida !== recebida) {
      if (!Number.isInteger(linha.conferida) || linha.conferida < 0) {
        recusar(linha.tipoId, new ErroDevolucao("Quantidade conferida inválida."));
      } else {
        disponivel = linha.conferida;
        const motivo = (linha.conferidaMotivo ?? "").trim();
        if (motivo === "") {
          recusar(linha.tipoId, new ErroDevolucao("Escolha o motivo da divergência."));
        } else {
          conferida = linha.conferida;
          conferidaMotivo = motivo;
        }
      }
    }
    conferidaPorTipo.set(linha.tipoId, { conferida, motivo: conferidaMotivo });
    disponivelPorTipo.set(linha.tipoId, disponivel);

    // --- excedente: `devolvida` é o total físico entregue nesta visita — pode
    // incluir Peças de carona do Backlog. O que a Comanda deste Lote ainda
    // podia render, depois de reservar o que ficou retido, é a Recebida (ou a
    // Conferida, se houver) menos `retidoLinha`; qualquer coisa acima disso só
    // pode ter vindo do Backlog. Derivado, nunca digitado (ADR-0019) — a
    // alocação é sempre FIFO. `Contagem.devolvida` grava só
    // a parte própria desta Comanda, igual sempre gravou — é o que a
    // Divergência (sempre contra a Recebida, nunca a Conferida — ADR-0021) e o
    // Faturamento esperam.
    const divisao = dividirFechamento(disponivel, linha.devolvida, retidoLinha);
    devolvidaPorTipo.set(linha.tipoId, divisao.devolvidaPropria);
    const retidaDigitada = retidoLinha + (pendentesPorTipo.get(linha.tipoId) ?? 0);
    const previa: PreviaTipo = {
      tipoId: linha.tipoId,
      divisao,
      carona: null,
      retencao: null,
      conta: contaDaPeca(disponivel, linha.devolvida, retidaDigitada, divisao.caronaDevolvida + divisao.caronaRetida),
    };
    tipos.set(linha.tipoId, previa);
    const excedente = divisao.caronaDevolvida + divisao.caronaRetida;
    if (excedente === 0) continue;

    // FIFO sobre o total que veio do Backlog; as Peças mais antigas são as
    // que voltaram ao Cliente, o resto é o que ficou retido de novo.
    const alocado = alocarFifo(partida, lote.clienteId, linha.tipoId, excedente, loteId, momento);
    // O Backlog Inicial só é preenchido pela tela (ADR-0027): a sobra não vira
    // saldo negativo sozinha — recusa e pede o cadastro.
    const semOrigem = excedente - alocado.reduce((a, x) => a + x.quantidade, 0);
    let aviso: Trecho[][] | null = null;
    if (semOrigem > 0) {
      // o que havia na data e o que entrou/saiu depois dela — a mesma conta
      // que a FIFO usa (backlogNoMomento, #86)
      const b = backlogNoMomento(partida, lote.clienteId, linha.tipoId, momento);
      aviso = avisoCaronaSemSaldo({
        falta: semOrigem,
        havia: b.noMomento,
        devolucao: { data: dados.data, hora: dados.hora || null },
        depois: b.entraramDepois,
        sairam: b.sairamDepois.map((s) => ({
          ...s,
          faturada: s.loteId != null && situacaoLote(snapshot, s.loteId) === "faturado",
        })),
      });
      recusar(linha.tipoId, new ErroSemSaldoBacklog(nomeDe(linha.tipoId), semOrigem, avisoCaronaEmTexto(aviso)), "carona");
    }
    previa.carona = { origem: alocado, falta: Math.max(0, semOrigem), aviso };
    if (semOrigem > 0) continue;
    let faltaDevolver = divisao.caronaDevolvida;
    for (const a of alocado) {
      const devolvidas = Math.min(a.quantidade, faltaDevolver);
      faltaDevolver -= devolvidas;
      const partes: [number, DestinoBaixa][] = [
        [devolvidas, "devolvida"],
        [a.quantidade - devolvidas, "retida"],
      ];
      for (const [quantidade, destino] of partes) {
        if (quantidade === 0) continue;
        if (a.origemLoteId === null) {
          baixasInicial.push({ loteId, clienteId: a.clienteId, tipoId: a.tipoId, quantidade, destino });
        } else {
          baixas.push({ loteId, origemLoteId: a.origemLoteId, tipoId: a.tipoId, motivo: a.motivo, quantidade, destino });
        }
      }
    }
  }

  // --- O que já saiu deste Sublote não volta a ele: Perda (ADR-0022) e
  // carona que outra OS levou (ADR-0019). Regravar a Devolução com uma
  // retenção menor que isso deixaria o saldo negativo para sempre, sem
  // Sublote para reabrir e devolver depois. Lê de `snapshot` (não de
  // `partida`): `limpar` só desfaz as baixas em que ESTE Lote é a carona,
  // nunca as que outros Lotes tiraram do Sublote dele, nem as Perdas.
  const saidoPorLinha = new Map<string, { tipoId: Id; motivo: string; perdido: number; carona: number }>();
  const somarSaida = (tipoId: Id, motivo: string, perdido: number, carona: number): void => {
    const k = chaveSaldo(loteId, tipoId, motivo);
    const atual = saidoPorLinha.get(k) ?? { tipoId, motivo, perdido: 0, carona: 0 };
    saidoPorLinha.set(k, { tipoId, motivo, perdido: atual.perdido + perdido, carona: atual.carona + carona });
  };
  for (const p of snapshot.perdas) {
    if (p.origemLoteId === loteId) somarSaida(p.tipoId, p.motivoOrigem, p.quantidade, 0);
  }
  for (const b of snapshot.baixasBacklog) {
    if (b.origemLoteId === loteId && b.loteId !== loteId) somarSaida(b.tipoId, b.motivo, 0, b.quantidade);
  }
  for (const [k, { tipoId, motivo, perdido, carona }] of saidoPorLinha) {
    const retido = retencoes
      .filter((r) => chaveSaldo(loteId, r.tipoId, r.motivo) === k)
      .reduce((a, r) => a + r.quantidade, 0);
    if (retido < perdido + carona) {
      const partes = [
        ...(carona > 0 ? [`${carona} já voltaram em outra OS`] : []),
        ...(perdido > 0 ? [`${perdido} foram registradas como perda`] : []),
      ];
      recusar(
        tipoId,
        new ErroDevolucao(
          `Das peças retidas no motivo "${motivo}" desta OS, ${partes.join(" e ")} — a retenção não pode ficar abaixo de ${perdido + carona}.`,
        ),
      );
    }
  }

  // --- monta o que seria gravado ---
  const contagens: ContagemLote[] = partida.contagens.map((c) => {
    if (c.loteId !== loteId) return c;
    const conf = conferidaPorTipo.get(c.tipoId);
    return {
      ...c,
      devolvida: devolvidaPorTipo.get(c.tipoId) ?? 0,
      conferida: conf?.conferida ?? null,
      conferidaMotivo: conf?.motivo ?? null,
      retidaInformada: c.retidaInformada,
    };
  });
  // linhas devolvidas de Tipos que não tinham contagem recebida — ou que não
  // têm Devolvida nenhuma, mas têm uma Conferida divergente mesmo assim (uma
  // divergência com Devolvida 0 não muda o excedente, mas ainda é um registro
  // que o motivo existe pra guardar; não é ruído a descartar).
  for (const [tipoId, devolvida] of devolvidaPorTipo) {
    const conf = conferidaPorTipo.get(tipoId);
    if (devolvida === 0 && conf?.conferida == null) continue;
    if (contagens.some((c) => c.loteId === loteId && c.tipoId === tipoId)) continue;
    contagens.push({
      loteId,
      tipoId,
      recebida: 0,
      devolvida,
      conferida: conf?.conferida ?? null,
      conferidaMotivo: conf?.motivo ?? null,
      retidaInformada: null,
    });
  }

  const gravado: Snapshot = {
    ...partida,
    contagens,
    retencoes: [...partida.retencoes, ...retencoes],
    baixasBacklog: [...partida.baixasBacklog, ...baixas],
    baixasBacklogInicial: [...partida.baixasBacklogInicial, ...baixasInicial],
    lotes: partida.lotes.map((l) =>
      l.id === loteId
        ? {
            ...l,
            devolvidoEm: dados.data,
            devolvidoHora: dados.hora || null,
            // fechou de novo: a OS deixa de estar reaberta para correção
            fechamentoAnteriorEm: null,
            fechamentoAnteriorHora: null,
            // o primeiro registro fica: regravar não muda a ordem (ADR-0028)
            fechamentoRegistradoEm: l.fechamentoRegistradoEm ?? opcoes.registradoEm ?? null,
            // o Fechamento registrado substitui o rascunho
            rascunhoFechamento: null,
          }
        : l,
    ),
  };

  // --- Situação da retenção de cada peça (ADR-0029): a Retida digitada na
  // Coleta (vazia vale 0) contra Backlog antes + (Conferida − Devolvida), e
  // toda peça que não voltou com motivo. No "Fechar OS" vira recusa.
  for (const c of gravado.contagens) {
    if (c.loteId !== loteId) continue;
    const linha = dados.linhas.find((l) => l.tipoId === c.tipoId);
    const s = situacaoRetencao({
      backlogAntes: backlogAntesDaDevolucao(partida, loteId, c.tipoId, devolucao),
      // o que o hotel declarou: peça retida além disso não está no papel dele (OS 684)
      recebida: c.recebida,
      disponivel: disponivelPorTipo.get(c.tipoId) ?? c.conferida ?? c.recebida,
      devolvida: linha?.devolvida ?? 0,
      classificadas: retidoDoLote(gravado, loteId, c.tipoId),
      pendentesDeMotivo: pendentesPorTipo.get(c.tipoId) ?? 0,
      retidaDigitada: c.retidaInformada,
    });
    const previa = tipos.get(c.tipoId) ?? {
      tipoId: c.tipoId,
      divisao: { devolvidaPropria: 0, caronaDevolvida: 0, caronaRetida: 0 },
      carona: null,
      retencao: null,
      conta: contaDaPeca(
        disponivelPorTipo.get(c.tipoId) ?? c.conferida ?? c.recebida,
        linha?.devolvida ?? 0,
        pendentesPorTipo.get(c.tipoId) ?? 0,
        0,
      ),
    };
    previa.retencao = s;
    tipos.set(c.tipoId, previa);
    if (opcoes.conferirRetencao && s.impedimento !== null) {
      recusar(c.tipoId, new ErroDevolucao(`${nomeDe(c.tipoId)}: ${s.recusa}`), "retencao");
    }
  }

  if (recusas.length > 0) return { tipos, recusas, resultado: null };

  // --- as OS devolvidas depois desta refazem a carona em ordem de devolução
  let out = gravado;
  for (const id of posteriores) {
    try {
      out = refazerFechamento(snapshot, out, id, { reordenar: false });
    } catch (e) {
      // o operador está fechando ESTA OS: a mensagem diz o que aconteceu com a
      // outra em termos de peça e data, não de recálculo
      const outra = snapshot.lotes.find((l) => l.id === id)!;
      const quando = formatarDevolucao(outra);
      recusar(
        null,
        e instanceof ErroSemSaldoBacklog
          ? new ErroDevolucao(
              `Esta OS foi devolvida antes da OS ${outra.os} (${quando}), que já está fechada. ` +
                `Pela ordem das devoluções, a OS ${outra.os} passa a ficar com ${e.falta} ${e.tipoNome} a mais do que havia no Backlog. ` +
                `Confira ${e.tipoNome} nesta OS e na OS ${outra.os}, ou cadastre o Backlog Inicial.`,
            )
          : new ErroDevolucao(
              `Esta OS foi devolvida antes da OS ${outra.os} (${quando}), que já está fechada e não pôde ser refeita: ${e instanceof Error ? e.message : String(e)}`,
            ),
      );
      return { tipos, recusas, resultado: null };
    }
  }
  return { tipos, recusas, resultado: out };
}

/**
 * Registra a Devolução de um Lote — e **encerra** o Lote. Regravar sobre uma
 * Devolução já feita substitui a anterior (vale até a OS ser faturada). É a
 * Prévia do Fechamento gravada: recusa com a primeira recusa dela.
 */
export function registrarDevolucao(
  snapshot: Snapshot,
  loteId: Id,
  dados: DadosDevolucao,
  opcoes: OpcoesFechamento = {},
): Snapshot {
  const { recusas, resultado } = preverFechamento(snapshot, loteId, dados, opcoes);
  if (recusas.length > 0) throw recusas[0]!.erro;
  return resultado!;
}

/**
 * A Prévia do Fechamento como a Ficha de OS precisa (#84): com a Coleta
 * digitada e ainda não salva (Recebida e Retida digitada) — o "Fechar OS"
 * salva a Coleta antes de fechar —, as mesmas conferências do "Fechar OS" e,
 * para OS ainda não registrada, a ordem de quem é registrado agora (depois
 * de todos os já registrados no empate de data e hora — ADR-0028).
 */
export function previaDaFicha(
  snapshot: Snapshot,
  loteId: Id,
  coleta: readonly { tipoId: Id; recebida: number; retidaInformada: number | null }[],
  fechamento: DadosDevolucao,
  hoje: DataISO,
): ReturnType<typeof preverFechamento> {
  let s = snapshot;
  for (const c of coleta) {
    const atual = s.contagens.find((x) => x.loteId === loteId && x.tipoId === c.tipoId);
    if ((atual?.recebida ?? 0) === c.recebida && (atual?.retidaInformada ?? null) === c.retidaInformada) continue;
    try {
      s = definirRecebida(s, loteId, c.tipoId, c.recebida, c.retidaInformada);
    } catch (e) {
      // número inválido na Coleta: quem recusa é o "Salvar" da Coleta
      if (!(e instanceof ErroLote)) throw e;
    }
  }
  const previa = preverFechamento(s, loteId, fechamento, {
    conferirRetencao: true,
    exigirHora: true,
    hoje,
    registradoEm: REGISTRO_PENDENTE,
  });
  // só prévia: o resultado tem o carimbo provisório de registro e a Coleta não
  // salva — nunca é o que se grava (o "Fechar OS" refaz tudo no servidor)
  return { ...previa, resultado: null };
}

/**
 * Tipos com saldo no Backlog do Cliente que a Ficha lista para esta OS —
 * pela base de onde o Fechamento parte (sem a devolução desta OS nem a
 * carona das OS devolvidas depois, ADR-0028): numa OS fechada fora de ordem,
 * a peça que uma OS posterior levou continua aparecendo. Só decide quais
 * peças aparecem; nenhum número vem daqui.
 */
export function tiposComBacklogNaFicha(
  snapshot: Snapshot,
  loteId: Id,
  clienteId: Id,
  devolucao: { data: string; hora: string | null },
): Set<Id> {
  if (!snapshot.lotes.some((l) => l.id === loteId)) return new Set();
  const base = baseDoFechamento(snapshot, loteId, { ...devolucao, registradoEm: REGISTRO_PENDENTE });
  return new Set(
    snapshot.tiposPeca.filter((t) => saldoDisponivel(base, clienteId, t.id, loteId) !== 0).map((t) => t.id),
  );
}

export interface ResultadoDevolucaoMassa {
  fechadas: number;
  falhas: { loteId: Id; motivo: string }[];
}

/**
 * Fecha vários Lotes de uma vez, assumindo Devolvida = Conferida = Recebida
 * em cada Tipo de Peça (sem divergência, sem retenção) — o mesmo que
 * "Preencher conforme comanda" faz numa OS por vez, aplicado em lote. A data
 * de cada Lote é o próprio prazo previsto dele (`previstoDoLote`), não uma
 * data única pro lote inteiro — cada OS fecha com a data que já era dela.
 *
 * Lote sem nenhuma Recebida, ou que o domínio recuse por outro motivo (ex.:
 * já faturado), é pulado com o motivo — os demais entram. Mesmo espírito de
 * `lancarLotesEmMassa`.
 */
export function registrarDevolucoesEmMassa(
  snapshot: Snapshot,
  loteIds: readonly Id[],
  hoje: DataISO,
  agora: string,
): { snapshot: Snapshot; resultado: ResultadoDevolucaoMassa } {
  let out = snapshot;
  let fechadas = 0;
  const falhas: ResultadoDevolucaoMassa["falhas"] = [];
  for (const loteId of loteIds) {
    try {
      const lote = out.lotes.find((l) => l.id === loteId);
      if (!lote) throw new ErroDevolucao("OS não encontrada.");
      if (emCorrecao(lote)) {
        // fechar "conforme comanda" passaria por cima da correção guardada
        throw new ErroDevolucao("OS reaberta para correção — feche pela Ficha da OS.");
      }

      const linhas: LinhaDevolucao[] = contagensDoLote(out, loteId)
        .filter((c) => c.recebida > 0)
        .map((c) => ({ tipoId: c.tipoId, devolvida: c.recebida, retidas: [] }));
      if (linhas.length === 0) {
        throw new ErroDevolucao("Nenhuma peça recebida nesta OS.");
      }

      const previsto = previstoDoLote(out, lote);
      if (!previsto) {
        // a hora da devolução é obrigatória (ADR-0028) e só o prazo a dá aqui
        throw new ErroDevolucao("Sem horário previsto — feche pela Ficha, informando a hora da devolução.");
      }
      const data = paraISO(previsto);
      if (depoisDeAmanha(data, hoje)) {
        // fechar "conforme comanda" com o prazo como data seria devolver no futuro
        throw new ErroDevolucao("Prazo ainda não chegou — feche pela Ficha quando devolver.");
      }

      out = registrarDevolucao(
        out,
        loteId,
        { data, linhas, ...(previsto ? { hora: horaDe(previsto) } : {}) },
        { conferirRetencao: true, exigirHora: true, registradoEm: agora, hoje },
      );
      fechadas += 1;
    } catch (err) {
      falhas.push({
        loteId,
        motivo: err instanceof Error ? err.message : "Não foi possível fechar.",
      });
    }
  }
  return { snapshot: out, resultado: { fechadas, falhas } };
}
